import { useMemo } from 'react';
import { useWebSocketConversation } from '@features/Admin/Campaign/hooks/useWebSocketConversation';
import { useMonitoringTranscriptions } from '@shared/hooks/useMonitoringTranscriptions';
import { ConversationActorsType } from '@shared/services/conversationApiService/apiService';
import { ExtendedConversationLogDto, ConversationMessagesReturn } from '../types';
import { CallActors } from '@shared/constants/recipient-status.constants';

export const useConversationMessages = (
  callId: string,
  isRoomConnected: boolean,
  onError?: <T>(error: T) => void,
): ConversationMessagesReturn => {
  const { initialLoading: loading, conversationData } = useWebSocketConversation(callId, onError);

  const { transcriptions: monitoringTranscriptions, debugInfo } = useMonitoringTranscriptions();

  const allMessages = useMemo(() => {
    const messages: ExtendedConversationLogDto[] = [...conversationData.data];

    if (isRoomConnected && monitoringTranscriptions.length > 0) {
      monitoringTranscriptions.forEach((segment, index) => {
        let actor: ConversationActorsType;
        let isMonitor = false;

        const participantType = (segment as any).participantType;
        if (participantType === 'agent') {
          actor = CallActors.AGENT;
        } else if (participantType === 'caller') {
          actor = CallActors.CUSTOMER;
        } else if (participantType === 'monitor') {
          actor = CallActors.CUSTOMER;
          isMonitor = true;
        } else {
          actor = CallActors.CUSTOMER;
        }

        const messageText = segment.text.trim();
        const existingMessage = messages.find(
          (msg) =>
            msg.message.trim() === messageText &&
            Math.abs(new Date(msg.createdAt).getTime() - segment.firstReceivedTime) < 2000,
        );

        if (!existingMessage && messageText.length > 0) {
          messages.push({
            id: `live-${segment.id || index}-${segment.firstReceivedTime}`,
            conversationId: callId,
            actor,
            message: segment.text,
            createdAt: new Date(segment.firstReceivedTime).toISOString(),
            updatedAt: new Date(segment.firstReceivedTime).toISOString(),
            isMonitor,
            isLive: true,
          });
        }
      });
    }

    return messages.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [conversationData.data, monitoringTranscriptions, isRoomConnected, callId]);

  return { allMessages, loading, debugInfo };
};
